import {
	Button,
	Dialog,
	Grid,
	H2,
	H3,
	H5,
	InfoChip,
	Label,
	Spacer,
	Text,
	TextInput,
	Tile,
	WarnChip
} from '@components';
import { BUTTON_TYPES, FREIGHT_MODE, INPUT_TYPE } from '@types';
import styles from './LinkedRateCardSummary.module.scss';
import LinkIcon from '@mui/icons-material/Link';
import { getFreightModeIcon, capitalizeFirstLetter } from '@utils';
import ArrowCircleRightOutlinedIcon from '@mui/icons-material/ArrowCircleRightOutlined';
import { LinkedRateCard, RateCardFamily, RateCard as RateCardState, GlobalRateCardId } from '@state';
import { useRecoilState, useRecoilValue } from 'recoil';
import EditOutlined from '@mui/icons-material/EditOutlined';
import { useIsInRoute } from '@hooks/useIsInRoute';
import { useEffect, useState } from 'react';
import { LinkedRateCardSchema } from './LinkedRateCardSchema';


export const LinkedRateCardSummary = () => {
	const [linkedRateCardData, setLinkedRateCardData] = useRecoilState(LinkedRateCard);
	const [isAlertInfoOpen, setIsAlertInfoOpen] = useState(false);
	const [newMargin, setNewMargin] = useState<string|null>();
	const [newMarginError, setNewMarginError] = useState<string>('');
	const rateCardId = useRecoilValue(GlobalRateCardId);
	const rateCard = useRecoilValue(
		rateCardId !== 'default' ? RateCardFamily(rateCardId) : RateCardState
	);
	const isLinked = useIsInRoute('linked');
	const isStep2 = useIsInRoute('step2');
	const canEditMargin = isLinked && isStep2;
	const MarginOnlySchema = LinkedRateCardSchema.pick({ margin_percentage: true, });

	const FreightModeIcon = getFreightModeIcon(rateCard?.freight_mode ?? FREIGHT_MODE.ROAD)

	const marginIsValid = (value: string) => {
		const result = MarginOnlySchema.safeParse({ margin_percentage: value });
		if (!result.success) {
			setNewMarginError(result?.error?.issues?.[0]?.message)
		} else {
			setNewMarginError('')
		}
	}

	useEffect(() => {
		if (newMargin) {
			marginIsValid(newMargin)
		}
	}, [newMargin])

	if (!linkedRateCardData) return;

	const updateMargin = (newMargin: string) => {
		setLinkedRateCardData({
			...linkedRateCardData,
			margin_percentage: newMargin,
		})
	}

	return (
		<Tile stretch>
			<div className={styles.header}>
				<H2>Linked Rate Card Summary</H2>
				<div className={styles.summary}>
					<WarnChip text={'Linked Rate Card'} showIcon={true} CustomIcon={LinkIcon} />
					<InfoChip CustomIcon={FreightModeIcon} text={capitalizeFirstLetter(rateCard?.freight_mode ?? FREIGHT_MODE.ROAD)} />
				</div>
			</div>
			<Spacer size={20} />
			<div className={styles.locations}>
				<H3>{rateCard?.origin_location?.text} <ArrowCircleRightOutlinedIcon color='warning'/> {rateCard?.destination_location?.text}</H3>
			</div>

			<Spacer size={20} />
			<Grid columns={3} columnTemplate={'repeat(3, 250px)'} rowGap={'4px'}>
				<Text text={'Carrier'} />
				<Text text={'Shipper'} />
				<Text text={'Margin %'} />
				<H5>{linkedRateCardData.carrier?.name}</H5>
				<H5>{linkedRateCardData.shipper?.name}</H5>
				<div className={styles.margin}>
					<H5>{linkedRateCardData.margin_percentage ?? 0}%</H5>
					{canEditMargin && <Button
						onClick={() => setIsAlertInfoOpen(true)}
						type={BUTTON_TYPES.ACTION}
						Icon={EditOutlined}
						text=''
					/>}
				</div>
			</Grid>

			<Dialog
				title={'Edit Margin %'}
				text={'This percentage will be applied as the margin for all shipments on this contracted lane.'}
				actionButtonText={'Update margin'}
				cancelButtonText={'Cancel'}
				open={isAlertInfoOpen}
				actionButtonDisabled={newMarginError !== ''}
				actionButtonFn={() => updateMargin(newMargin ? newMargin?.toString() : '')}
				actionButtonLoading={false}
				onClose={() => setIsAlertInfoOpen(false)}
				slot={<>
					<div>
						<Label text={'Margin %'} required/>
						<TextInput
							validateAsNumber
							type={INPUT_TYPE.NUMBER}
							name={'margin'}
							placeholder={'Enter percentage (e.g., 1.5) %'}
							value={newMargin?.toString() ?? ''}
							hasError={newMarginError !== ''}
							errorMessage={newMarginError}
							onChange={(e) => {
								const value = parseFloat(e.target.value);
								if (!isNaN(value)) {
									setNewMargin(value.toString())
								} else {
									setNewMargin('')
								}
							}}
						/>
					</div>
				</>}
			/>
		</Tile>
	)
}